import { Badge, Button, Input, Select, Switch, Text, Textarea, toast } from "@medusajs/ui"
import { useState } from "react"
import { FilePicker } from "@/components/file-picker"
import { Field } from "@/components/form"
import { EmptyState, ErrorState, PageHeader, SearchInput, Section } from "@/components/ui"
import { useDeleteLot, useLots, useSaveLot, useVariantSearch, type Lot, type LotInput } from "@/lib/lots"
import { useCan } from "@/lib/permissions"
import { errorMessage } from "@/lib/sdk"

/**
 * Products → Lots and COAs. A lot is a lot number for one product option with its Certificate of
 * Analysis (a PDF from Content → Files). One lot per option can be "current": fulfilled orders get it
 * automatically on each line that has no lot yet; staff can change any order's lots on the order page.
 * Customers see the lot number and the COA download in their account and subscription portal.
 */
export function LotsPage() {
  const [q, setQ] = useState("")
  const { data, isLoading, error } = useLots(q)
  const can = useCan()("products:write")
  const [editing, setEditing] = useState<Lot | "new" | null>(null)
  if (error) return <ErrorState error={error} />
  return (
    <>
      <PageHeader title="Lots and COAs" subtitle="Lot numbers and Certificates of Analysis per product option" actions={can && <Button size="small" onClick={() => setEditing("new")}>Add lot</Button>} />
      {editing ? <LotForm lot={editing === "new" ? null : editing} onClose={() => setEditing(null)} /> : null}
      <Section>
        <div className="px-4 py-3">
          <SearchInput value={q} onChange={setQ} placeholder="Search lot number, SKU or product" />
        </div>
        {isLoading ? (
          <Text size="small" className="p-4">Loading…</Text>
        ) : !data?.lots.length ? (
          <EmptyState title="No lots yet" body="Add a lot for each batch you receive, with its Certificate of Analysis." />
        ) : (
          <table className="w-full text-[13px]">
            <thead className="border-y border-ui-border-base text-left text-ui-fg-subtle">
              <tr>
                <th className="px-4 py-2 font-medium">Lot number</th>
                <th className="px-4 py-2 font-medium">Product option</th>
                <th className="px-4 py-2 font-medium">COA</th>
                <th className="px-4 py-2 font-medium">Expires</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {data.lots.map((l) => (
                <tr key={l.id} className="border-b border-ui-border-base last:border-0">
                  <td className="px-4 py-2 font-mono">
                    {l.lot_number} {l.is_current ? <Badge size="2xsmall" color="green">Current</Badge> : null}
                  </td>
                  <td className="px-4 py-2">
                    {l.title}
                    {l.sku ? <span className="ml-1 font-mono text-ui-fg-subtle">{l.sku}</span> : null}
                  </td>
                  <td className="px-4 py-2">{l.coa_url ? <a href={l.coa_url} target="_blank" rel="noreferrer" className="text-ui-fg-interactive hover:underline">{l.coa_name || "View PDF"}</a> : <span className="text-ui-fg-muted">None</span>}</td>
                  <td className="px-4 py-2 font-mono">{l.expires_on ?? ""}</td>
                  <td className="px-4 py-2 text-right">{can ? <Button size="small" variant="transparent" onClick={() => setEditing(l)}>Edit</Button> : null}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>
    </>
  )
}

function LotForm({ lot, onClose }: { lot: Lot | null; onClose: () => void }) {
  const save = useSaveLot()
  const del = useDeleteLot()
  const [f, setF] = useState<LotInput>(() => (lot ? { lot_number: lot.lot_number, coa_url: lot.coa_url, coa_name: lot.coa_name, manufactured_on: lot.manufactured_on, expires_on: lot.expires_on, notes: lot.notes, is_current: lot.is_current } : { is_current: true }))
  const [search, setSearch] = useState("")
  const variants = useVariantSearch(search)
  const [picking, setPicking] = useState(false)
  const set = (patch: LotInput) => setF({ ...f, ...patch })
  const submit = async () => {
    try {
      await save.mutateAsync(lot ? { id: lot.id, ...f } : f)
      toast.success(lot ? "Lot saved" : "Lot added")
      onClose()
    } catch (e) {
      toast.error(errorMessage(e))
    }
  }
  return (
    <Section title={lot ? `Edit lot ${lot.lot_number}` : "Add lot"}>
      <div className="grid gap-4 p-4 sm:grid-cols-2">
        <Field label="Lot number">
          <Input value={f.lot_number ?? ""} maxLength={40} onChange={(e) => set({ lot_number: e.target.value })} className="font-mono" />
        </Field>
        {lot ? (
          <Field label="Product option">
            <Text size="small" className="pt-2">{lot.title}</Text>
          </Field>
        ) : (
          <Field label="Product option" hint="Search a product, then pick its option (pack size).">
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search products" />
            <Select value={f.variant_id ?? ""} onValueChange={(v) => set({ variant_id: v })}>
              <Select.Trigger className="mt-2"><Select.Value placeholder="Choose an option" /></Select.Trigger>
              <Select.Content>
                {(variants.data?.products ?? []).flatMap((p) =>
                  p.variants.map((v) => (
                    <Select.Item key={v.id} value={v.id}>
                      {p.title} · {v.title}{v.sku ? ` (${v.sku})` : ""}
                    </Select.Item>
                  )),
                )}
              </Select.Content>
            </Select>
          </Field>
        )}
        <Field label="Certificate of Analysis (PDF)" hint="Upload it in Content → Files, then choose it here.">
          <div className="flex items-center gap-2">
            <Input value={f.coa_url ?? ""} onChange={(e) => set({ coa_url: e.target.value || null })} placeholder="https://…" />
            <Button size="small" variant="secondary" onClick={() => setPicking(true)}>Choose file</Button>
          </div>
        </Field>
        <Field label="COA link text (optional)">
          <Input value={f.coa_name ?? ""} maxLength={120} onChange={(e) => set({ coa_name: e.target.value || null })} placeholder="Certificate of Analysis" />
        </Field>
        <Field label="Manufactured on">
          <Input type="date" value={f.manufactured_on ?? ""} onChange={(e) => set({ manufactured_on: e.target.value || null })} />
        </Field>
        <Field label="Expires on">
          <Input type="date" value={f.expires_on ?? ""} onChange={(e) => set({ expires_on: e.target.value || null })} />
        </Field>
        <Field label="Notes (staff only)">
          <Textarea value={f.notes ?? ""} maxLength={1000} onChange={(e) => set({ notes: e.target.value || null })} />
        </Field>
        <label className="flex items-start justify-between gap-4 sm:pt-6">
          <div>
            <Text size="small">Current lot for this option</Text>
            <Text size="xsmall" className="text-ui-fg-subtle">Fulfilled orders get it automatically. Only one lot per option is current.</Text>
          </div>
          <Switch checked={!!f.is_current} onCheckedChange={(v) => set({ is_current: v })} />
        </label>
        <div className="flex flex-wrap gap-2 sm:col-span-2">
          <Button size="small" isLoading={save.isPending} onClick={submit}>{lot ? "Save lot" : "Add lot"}</Button>
          <Button size="small" variant="secondary" onClick={onClose}>Cancel</Button>
          {lot ? (
            <Button
              size="small"
              variant="danger"
              className="ml-auto"
              onClick={async () => {
                try {
                  await del.mutateAsync(lot.id)
                  toast.success("Lot deleted")
                  onClose()
                } catch (e) {
                  toast.error(errorMessage(e))
                }
              }}
            >
              Delete
            </Button>
          ) : null}
        </div>
      </div>
      {picking ? (
        <FilePicker
          images={false}
          title="Choose the COA PDF"
          onClose={() => setPicking(false)}
          onDone={(files) => {
            const file = files[0]
            if (file) set({ coa_url: file.url, coa_name: f.coa_name || null })
            setPicking(false)
          }}
        />
      ) : null}
    </Section>
  )
}
